import type { PropsWithChildren, ReactNode, RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import _TooltipTrigger from './_TooltipTrigger'
import BodyText from './Typography/BodyText'
import TextLink from './TextLink'
import LineTimesIcon from './Icons/line/LineTimesIcon'
import { autoUpdate, flip, offset, shift, useFloating, arrow as arrowMiddleware } from '@floating-ui/react-dom'
import zIndex from 'styles/tools/z-index'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'
import { stopPropagation } from 'lib/web/eventHandlerUtils'
import clsx from 'clsx'

/** in ms */
const TOOLTIP_TRANSITION = 200

export type Placement =
  | 'top' | 'top-start' | 'top-end'
  | 'right' | 'right-start' | 'right-end'
  | 'bottom' | 'bottom-start' | 'bottom-end'
  | 'left' | 'left-start' | 'left-end'

interface Props extends PropsWithChildren {
  /** Whether the tooltip is disabled and should not be interactive */
  disabled?: boolean
  /** Additional CSS class name to apply to the tooltip trigger */
  className?: string
  /** Whether to render the tooltip in a portal (document.body) instead of inline */
  portaled?: boolean
  /** Main title content displayed in the tooltip */
  title?: ReactNode
  /** Secondary description text displayed below the title */
  description?: ReactNode
  /** Label text for the action button/link */
  actionLabel?: string
  /** URL for the action link (if provided, renders as a link instead of button) */
  actionHref?: string
  /** Callback function called when the action button/link is clicked */
  onActionClick?: () => void
  /** Icon component to display at the start of the tooltip content */
  leadingIcon?: ReactNode
  /** Whether the tooltip can be dismissed by the user (defaults to true) */
  dismissible?: boolean
  /** Callback function called when the tooltip is dismissed */
  onDismiss?: () => void
  /** Position of the tooltip relative to the trigger element (defaults to 'right') */
  placement?: Placement
  /** Reference to the element that the tooltip should be positioned relative to (overrides children) */
  anchorRef?: RefObject<HTMLElement | null>
  /** Reference to the boundary element that constrains the tooltip positioning */
  boundaryRef?: RefObject<HTMLElement | null>
  /** Controlled open state of the tooltip */
  open?: boolean
  /**
   * Visual variant of the tooltip.
   * @default 'light'
   */
  variant?: 'light' | 'dark'
  /** Also show/hide on mouseenter/mouseleave of the trigger */
  showOnHover?: boolean
  /**
   * [Uncontrolled] Initial open state.
   * @default false
   */
  defaultOpen?: boolean
  /** Close on outside click even when `dismissible` is true */
  closeOnOutsideClick?: boolean
  /** Force pointer-events: auto on the trigger (useful inside pointer-events: none containers) */
  forcePointerEvents?: boolean
  /** Override the default max-width of the tooltip (in px) */
  maxWidth?: number
  /** Stop trigger clicks bubbling to parent click handlers */
  stopClickPropagation?: boolean
}

const Sheet = styled.div<{ $maxWidth?: number }>`
  background-color: ${p => p.theme.palette.highlight.primary.lightBackground};
  color: ${p => p.theme.palette.highlight.primary.normalContrast};
  border-radius: ${p => p.theme.borderRadius.S};
  box-shadow: ${p => p.theme.shadow.bottom.medium};
  padding: ${rem(8)};
  width: max-content;
  max-width: min(${p => rem(p.$maxWidth ?? 500)}, calc(100vw - ${rem(32)}), 65vw);
  min-width: ${rem(130)};
  z-index: ${zIndex.tooltip};
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity ${TOOLTIP_TRANSITION}ms, visibility 0s ${TOOLTIP_TRANSITION}ms;

  &.visible { pointer-events: auto; opacity: 1; visibility: visible; transition: opacity ${TOOLTIP_TRANSITION}ms, visibility 0s; }
  &.not-opened-once { display: none; }

  &.variant-dark {
    background-color: ${p => p.theme.palette.neutral.surface.darkBackground};
    color: ${p => p.theme.palette.neutral.surface.darkContrast};
  }

  position: absolute;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: ${rem(8)};
  align-items: start;
`

const IconSlot = styled.div`
  line-height: 0;
  margin-top: ${rem(2)};
`

const CloseButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: inherit;
`

const Content = styled.div`
  display: grid;
  gap: ${rem(4)};
  align-self: center;
`

const Arrow = styled.div`
  position: absolute;
  width: ${rem(10)};
  height: ${rem(10)};
  background: ${p => p.theme.palette.highlight.primary.lightBackground};
  transform: rotate(45deg);
  box-shadow: none;
  pointer-events: none;

  &.variant-dark {
    background: ${p => p.theme.palette.neutral.surface.darkBackground};
  }
`

function RichTooltip(props: Props) {
  const {
    children,
    className,
    disabled,
    portaled,
    title,
    description,
    actionLabel,
    actionHref,
    onActionClick,
    leadingIcon,
    dismissible = true,
    onDismiss,
    placement = 'right',
    anchorRef,
    boundaryRef,
    open,
    variant = 'light',
    showOnHover,
    defaultOpen = false,
    closeOnOutsideClick,
    forcePointerEvents,
    maxWidth,
    stopClickPropagation,
  } = props

  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const arrowRef = useRef<HTMLDivElement | null>(null)

  const [internalOpen, setInternalOpen] = useState<boolean>(defaultOpen)
  const isControlled = open !== undefined
  const isOpen = open ?? internalOpen
  const [hasOpenedOnce, setHasOpenedOnce] = useState<boolean>(Boolean(isOpen))

  const float = useFloating<HTMLElement>({
    placement,
    strategy: 'absolute',
    middleware: [
      offset(8),
      shift({ padding: 8, boundary: boundaryRef?.current ?? undefined }),
      flip({ crossAxis: false, flipAlignment: true, padding: 16, boundary: boundaryRef?.current ?? undefined }),
      arrowMiddleware({ element: arrowRef, padding: 8 }),
    ],
  })

  const intl = useIntl()

  const [side, align] = useMemo(() => {
    const [s, a] = float.placement.split('-') as [string, string | undefined]
    return [s, a ?? 'center'] as const
  }, [float.placement])

  const openTooltip = useCallback(() => {
    if (!isControlled) setInternalOpen(true)
  }, [isControlled])

  const closeTooltip = useCallback(() => {
    if (!isControlled) setInternalOpen(false)
  }, [isControlled])

  useEffect(() => {
    const referenceElement = anchorRef?.current ?? triggerRef.current
    if (isOpen && referenceElement && tooltipRef.current) {
      if (!hasOpenedOnce) setHasOpenedOnce(true)
      return autoUpdate(referenceElement, tooltipRef.current, float.update)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRef, isOpen])

  useEffect(() => {
    if (anchorRef?.current ?? triggerRef.current) {
      float.refs.setReference(anchorRef?.current ?? triggerRef.current)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [float.refs.setReference, triggerRef, anchorRef])

  useEffect(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const onClick = (e: Event) => {
      e.preventDefault()
      if (stopClickPropagation) e.stopPropagation()
      openTooltip()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openTooltip()
      }
    }
    trigger.addEventListener('click', onClick)
    trigger.addEventListener('keydown', onKeyDown)

    if (showOnHover) {
      trigger.addEventListener('mouseenter', openTooltip)
      trigger.addEventListener('mouseleave', closeTooltip)
    }

    return () => {
      trigger.removeEventListener('click', onClick)
      trigger.removeEventListener('keydown', onKeyDown)
      if (showOnHover) {
        trigger.removeEventListener('mouseenter', openTooltip)
        trigger.removeEventListener('mouseleave', closeTooltip)
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [stopClickPropagation])

  useEffect(() => {
    if (!isOpen || isControlled) return
    if (!closeOnOutsideClick && dismissible) return
    const onDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (tooltipRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      closeTooltip()
    }
    document.addEventListener('mousedown', onDocumentMouseDown)
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown)
    }
  }, [isOpen, dismissible, isControlled, closeTooltip, closeOnOutsideClick])

  const element = <Sheet
    role="tooltip"
    $maxWidth={maxWidth}
    ref={(el) => { tooltipRef.current = el; float.refs.setFloating(el) }}
    className={clsx(
      className,
      `variant-${variant}`,
      {
        visible: isOpen,
        'not-opened-once': !hasOpenedOnce,
      },
    )}
    style={{ position: float.strategy, top: float.y ?? 0, left: float.x ?? 0 }}
    onClick={stopPropagation}
    data-side={side}
    data-align={align}
  >
    <Row>
      {leadingIcon && <IconSlot>{leadingIcon}</IconSlot>}
      <Content>
        {title && <BodyText variant="large" weight="bold">{title}</BodyText>}
        {description && <BodyText variant="small" weight="normal">{description}</BodyText>}
        {actionLabel && (
          <TextLink size="caption" onClick={onActionClick} href={actionHref} colour="inherit">
            {actionLabel}
          </TextLink>
        )}
      </Content>
      {dismissible && (
        <CloseButton aria-label={intl.formatMessage({ id: 'Cltcna8PlaqBsYR4loYP', defaultMessage: 'Dismiss', description: 'Accessibility aria-label for the close button in the tooltip' })} onClick={() => { onDismiss?.(); closeTooltip() }}>
          <LineTimesIcon size="S" />
        </CloseButton>
      )}
    </Row>
    {/**
     * The arrow is positioned by Floating UI via middleware data.
     * We offset it outward by half its size (5px) on the static side so it "points" to the trigger.
     */}
    <Arrow
      ref={arrowRef}
      className={`variant-${variant}`}
      style={{
        left: float.middlewareData.arrow?.x != null ? `${float.middlewareData.arrow.x}px` : '',
        top: float.middlewareData.arrow?.y != null ? `${float.middlewareData.arrow.y}px` : '',
        [({ top: 'bottom', right: 'left', bottom: 'top', left: 'right' } as const)[side as 'top' | 'right' | 'bottom' | 'left']]: '-5px',
      } as React.CSSProperties}
      data-side={side}
    />
  </Sheet>

  if (portaled) {
    if (typeof document !== 'undefined') {
      return <>
        <_TooltipTrigger ref={triggerRef} className={className} disabled={disabled} forcePointerEvents={forcePointerEvents}>{children}</_TooltipTrigger>
        {!disabled && createPortal(element, document.body)}
      </>
    }
    return null
  }

  return <>
    <_TooltipTrigger ref={triggerRef} className={className} disabled={disabled} forcePointerEvents={forcePointerEvents}>{children}</_TooltipTrigger>
    {!disabled && element}
  </>
}

export default RichTooltip
